<template lang="pug">
.my-4
  data-handler(
    v-model="initialData"
    :method="initData"
    show-loading
  )
    template(#loading="{ loading }")
      .text-center
        v-progress-circular(
          color="primary"
          indeterminate
        )

    template(#default="{ init }")
      template(v-if="hasErrors")
        v-alert(
          type="error"
          color="redesignError"
          rounded="xl"
          prominent
        )
          v-row(align="center")
            v-col.grow(
              :sm="12"
              :md="6"
            )
              h3 Невозможно подать заявку
              p.mt-1 {{ registerError.message }}
            v-spacer
            v-col.shrink(cols="auto")
              v-btn(:to="`/event/view/${event.id}`") Перейти на страницу мероприятия

      template(v-else)
        validation-observer(
          ref="globalObs"
          slim
        )
          app-stepper(
            v-model="currentStep"
            flat
          )
            template(v-if="visibleRegisterStepsList.length > 1")
              v-stepper-header
                template(v-for="(step, index) in visibleRegisterStepsList")
                  app-stepper-step(
                    :key="`${step.id}-step`"
                    v-bind="step.attrs({ obs: $refs.globalObs })"
                    :step="index + 1"
                  )
                    span {{ step.name }}
                  template(v-if="visibleRegisterStepsList.length > step.id")
                    v-divider

            v-stepper-items
              template(v-for="(step, index) in visibleRegisterStepsList")
                v-stepper-content(
                  :key="`${step.id}-content`"
                  :step="index + 1"
                )
                  component(
                    v-model="formData[step.id]"
                    v-bind="step.component_attrs"
                    v-on="step.component_listeners"
                    :is="step.component"
                    :method="(...params) => callRegister(...params, init)"
                    :global-fields="visibleGlobalFields"
                    :step-id="step.id"
                    :vid="step.vid"
                    :event="event"
                    :key="step.key"
                  )

                  portal(
                    :to="`${step.id}-actions`"
                    v-slot="stepProps"
                  )
                    template(v-if="hasMoreSteps")
                      app-button(
                        :disabled="stepProps.invalid || !hasMoreSteps"
                        rounded
                        @click="next"
                      ).ml-3 Далее
                    template(v-if="canSubmitApplications")
                      app-button(
                        :disabled="stepProps.invalid"
                        :loading="stepProps.loading"
                        rounded
                        @click="stepProps.action"
                      ).ml-3 Подать {{ $tc('requests.score.dative', selectedNominations.length) }}
</template>

<script>
// TODO: Изменить архитектуру регистрации и обновить базу компонентов перед этим.
// WARNING: Если ты зашёл что-то поправить, зови @MRXYZ

import MainInfoForm from './steps/MainInfoForm.vue';
import RequirementsForm from './steps/RequirementsForm.vue';
import AdditionalInfoForm from './steps/AdditionalInfoForm.vue';
import EventAgreement from '@/components/global/event-agreements/EventAgreement.vue';
import mixinForceRerender from '@/mixins/mixinForceRerender';
import { PER_PAGE } from '@/constants/pagination';

const REGISTER_STEPS = {
  requirements: 1,
  mainInfo: 2,
  additionalInfo: 3,
};

const REGISTER_ERRORS = {
  no_nominations: 1,
  no_passing_by_age: 2,
  no_started: 3,
  closed: 4,
  ended: 5,
};

export default {
  name: 'EventRegisterStepper',
  mixins: [mixinForceRerender],
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      formData: {},
      initialData: {
        applications: [],
        eventFields: [], // attributes
        eventGroups: [], // groups
      },
      currentStep: REGISTER_STEPS.requirements,
      requirementsData: {},
    };
  },
  computed: {
    eventFields() {
      const { eventFields } = this.initialData;
      return eventFields;
    },
    showNominationsSelect() {
      const { event, initialData } = this;
      const { eventGroups } = initialData;
      const { has_nominations, is_manual_group_management } = event.options;
      return has_nominations || (!is_manual_group_management && eventGroups.length > 1);
    },
    registerStepsData() {
      const { mixinRerenderTrigger, nominationsWithForms, hasAdditionalInfo, isEditableStep, initialData, showNominationsSelect } = this;

      const { applications, eventGroups, eventFields } = initialData;

      return {
        [REGISTER_STEPS.requirements]: {
          id: REGISTER_STEPS.requirements,
          key: 'requiremenents',
          name: 'Требования',
          component: RequirementsForm,
          show: true,
          get vid() {
            return `step-obs-${this.id}`;
          },
          attrs({ obs }) {
            return {
              editable: isEditableStep(obs, this.id),
            };
          },
          component_listeners: {
            fieldsData: (val) => {
              this.requirementsData = { ...this.requirementsData, ...val };
            },
            updated: () => {
              this.mixinRerenderTrigger += 1;
            },
          },
        },
        [REGISTER_STEPS.mainInfo]: {
          id: REGISTER_STEPS.mainInfo,
          key: `main-info-${mixinRerenderTrigger}`,
          name: 'Основная информация',
          component: MainInfoForm,
          component_attrs: {
            applications,
            eventGroups,
          },
          show: showNominationsSelect,
          get vid() {
            return `step-obs-${this.id}`;
          },
          attrs({ obs }) {
            return {
              editable: isEditableStep(obs, this.id),
            };
          },
          component_listeners: {
            fieldsData: (val) => {
              this.requirementsData = { ...this.requirementsData, ...val };
            },
          },
        },
        [REGISTER_STEPS.additionalInfo]: {
          id: REGISTER_STEPS.additionalInfo,
          key: 'additional-info',
          name: 'Дополнительная информация',
          component: AdditionalInfoForm,
          component_attrs: {
            eventFields,
            nominations: nominationsWithForms,
          },
          show: hasAdditionalInfo,
          get vid() {
            return `step-obs-${this.id}`;
          },
          attrs({ obs }) {
            return {
              editable: isEditableStep(obs, this.id),
            };
          },
        },
      };
    },
    registerStepsList() {
      const { registerStepsData } = this;
      return Object.values(registerStepsData);
    },
    // TODO: использовать в будущем метод из модели event
    // предположительно он должен масштабироваться, чтобы дополнять его условиями
    registerErrorsData() {
      const { event } = this;

      return {
        [REGISTER_ERRORS.no_nominations]: {
          id: REGISTER_ERRORS.no_nominations,
          message: `
            К сожалению, больше нет
            ${event.options.frontend_params.getEntityName('nominations_override', { counted: true, count: 5 })},
            на которые можно подать заявку
          `,
        },
        [REGISTER_ERRORS.no_passing_by_age]: {
          id: REGISTER_ERRORS.no_passing_by_age,
          message: 'К сожалению, вы не проходите по возрасту на данное мероприятие',
        },
        [REGISTER_ERRORS.no_started]: {
          id: REGISTER_ERRORS.no_started,
          message: 'Запись ещё не началась',
        },
        [REGISTER_ERRORS.closed]: {
          id: REGISTER_ERRORS.closed,
          message: 'Мероприятие завершено',
        },
        [REGISTER_ERRORS.ended]: {
          id: REGISTER_ERRORS.ended,
          message: 'Запись завершена',
        },
      };
    },
    globalFields() {
      const { event, canSubmitApplications, requirementsData } = this;
      const fieldsNames = Object.values(requirementsData).map((i) => i.text);

      return [
        {
          hidden: !canSubmitApplications,
          value: 'argreement',
          rules: 'required',
          component: EventAgreement,
          component_attrs: {
            event,
            fieldsNames,
            class: ['mb-6', 'px-3'],
            hideDetails: true,
          },
        },
      ];
    },
    visibleGlobalFields() {
      return this.globalFields.filter((f) => !f.hidden);
    },
    registerError() {
      const { errorId } = this;
      return this.registerErrorsData[errorId];
    },
    errorId() {
      const { $user, event, canRegister } = this;

      if (!canRegister) {
        if (event.isClosed) return REGISTER_ERRORS.closed;
        if (event.options.isRegisterEnd) return REGISTER_ERRORS.ended;
        if (event.options.isRegisterNotStart) return REGISTER_ERRORS.no_started;
        if (!event.isUserPassingByAge && $user?.profile?.birthday_at) {
          return REGISTER_ERRORS.no_passing_by_age;
        }
        return REGISTER_ERRORS.no_nominations;
      }

      return null;
    },
    hasErrors() {
      return !!this.registerError;
    },
    visibleRegisterStepsList() {
      const { registerStepsList } = this;
      return registerStepsList.filter((step) => step.show);
    },
    selectedNominations() {
      const { formData } = this;
      return formData[REGISTER_STEPS.mainInfo]?.nominations || [];
    },
    nominationsWithForms() {
      // Получаю только номинации с формами
      // Это нужно для отображения состояния номинации в шаге "Дополнительная информация"

      const { event, selectedNominations, eventFields } = this;

      return selectedNominations.filter(({ id }) => !!eventFields.filter((item) => item.isAssigned(event.id, id) || item.isGlobal(event.id)).length);
    },
    hasMoreSteps() {
      return this.visibleRegisterStepsList.length > this.currentStep;
    },
    hasAdditionalInfo() {
      return !!this.nominationsWithForms.length;
    },
    canSubmitApplications() {
      return !this.hasMoreSteps && this.selectedNominations.length;
    },
    // Может попросить Пашу сделать проверку Boolean, чтобы не перебирать на фронте?
    canCreateNewApplication() {
      const { event, initialData } = this;
      const { applications } = initialData;
      const applicationNominationIds = applications.map(({ nomination_id }) => nomination_id);
      const notCreatedApplications = event.actualNominations.filter(({ id }) => !applicationNominationIds.includes(id));

      return notCreatedApplications.length > 0;
    },
    canRegister() {
      const { $user, event, canCreateNewApplication } = this;

      return event.options.isRegisterPeriod && (event.isUserPassingByAge || !$user?.profile?.birthday_at) && canCreateNewApplication;
    },
  },
  watch: {
    'initialData.eventGroups': {
      handler(groups) {
        // Если нет номинаций, то передаем основную информацию
        if (!this.showNominationsSelect) {
          const { options, actualNominations } = this.event;
          const { formData } = this;
          this.$set(formData, REGISTER_STEPS.mainInfo, {
            nominations: actualNominations.map((nomination) => ({
              ...nomination,
              ...!options.is_manual_group_management ? { selected_groups: [...groups] } : {},
            })),
          });
        }
      },
      deep: true,
    },
    // formData: {
    //   handler(val) {
    //     console.log(val);
    //   },
    //   deep: true,
    // },
  },
  methods: {
    async initData() {
      const promises = [this.getUserEventApplications(), this.getUserEventInfoRequests(), this.getEventGroups()];

      const [applications, eventFields, eventGroups] = await Promise.all(promises);

      return new Promise((resolve) => {
        const data = {
          data: {
            applications: applications?.data?.data || [],
            eventFields: eventFields?.items || [],
            eventGroups: eventGroups?.items || [],
          },
          result_code: 'FLSCS',
        };
        const headers = { 'x-pagination-per-page': PER_PAGE.infinity };
        resolve({ data, headers });
      });
    },
    getUserEventInfoRequests() {
      return this.event.getAttributes({
        'per-page': PER_PAGE.infinity,
      });
    },
    getUserEventApplications() {
      const { event } = this;

      return this.$api.user.getUserEventRequests({
        'search[event_id]': event.id,
        'per-page': PER_PAGE.infinity,
      });
    },
    getEventGroups() {
      const { event } = this;
      const params = {
        'search[event_id]': event.id,
        'per-page': PER_PAGE.infinity,
      };

      return this.$models.group.objects.index({ params });
    },
    async register(init) {
      const { event, selectedNominations } = this;
      const nominations = selectedNominations.map((i) => {
        const { id } = i;
        const [group_id] = i.selected_groups?.map((g) => g.id) || [];

        return { id, group_id };
      });
      const payload = {
        args: [event.id],
        data: {
          nominations,
        },
      };
      await this.$rApi.events.registerCertificate(payload).then(() => {
        const pluralSentence = this.$tc('requests.score.direct', selectedNominations.length);
        const successMessage = `${pluralSentence} организатору мероприятия`;
        this.$toast.success(successMessage);
        this.$router.push({ name: 'event-register-successful-redesign', params: { id: event.id } });
        this.reset();
        init();
      });
    },
    callRegister(args, method, init) {
      let promise = method;
      if (!promise) {
        promise = () => new Promise((res) => res(true));
      }

      return new Promise((res, rej) => {
        promise(args)
          .then(async (resp) => {
            if (!resp.error) {
              await this.register(init);
              res(resp);
              return;
            }

            throw resp.error;
          })
          .catch((e) => rej(e));
      });
    },
    isEditableStep(obs, id) {
      const { currentStep } = this;
      const observers = obs?.observers || [];
      const { vid } = this.registerStepsData[currentStep];
      const stepObs = observers.find((i) => i.id === vid);

      return stepObs?.flags?.valid || currentStep === id || (currentStep > id && !stepObs?.flags?.failed);
    },
    next() {
      const { currentStep, registerStepsList } = this;

      if (currentStep < registerStepsList.length) {
        this.currentStep += 1;
      }
    },
    reset() {
      this.formData = {};
      this.$refs.globalObs.reset();
    },
  },
};
</script>
